import Vue from 'vue'
import { AssetId, AssetUrl, AssetTypeEnum, ContentAsset, Item, UserRole } from 'able-lib'
import _ from 'lodash'
import { axiosHttp } from 'boot/axios'
import moment from 'moment'
import { TOKEN_NAME } from 'src/lib/constants'
import { BucketItem } from 'minio'
import { format } from 'quasar'
import deployment from 'src/lib/deployment.json'
import { EventBus } from 'src/lib/eventBus'
const { humanStorageSize } = format

declare const document
declare const localStorage

export function getAssetUrlsOnItems (items: Array<Item>): Array<{id: number; url: Array<AssetUrl>}> {
  const supportedFileType: Array<string> = ['pdf', 'mp4', 'mp3']
  const assetUrls = []
  items.forEach((item: Item) => {
    item.items.forEach((itemChild: Item) => {
      itemChild.content.contentAssets.forEach((contentAsset: ContentAsset) => {
        const splitUrl = contentAsset.asset.url.split('.')
        const type = splitUrl[splitUrl.length - 1]
        if (supportedFileType.includes(type)) {
          assetUrls.push({
            id: item.id,
            url: [contentAsset.asset.url]
          })
        }
      })
    })
  })
  const lessonAssets = assetUrls.reduce((asset, { id, url }) => {
    asset[id] = asset[id] || { id, url: [] }
    return {
      ...asset,
      ...{
        [id]: {
          id,
          url: asset[id].url.concat(url)
        }
      }
    }
  }, {})
  return Object.values(lessonAssets)
}

export function getSecuredAssetUrl (url): string {
  return url + '?token=' + localStorage.getItem(TOKEN_NAME)
}

export function formatAttachments (attachments) {
  const formattedAttachments = []
  attachments.forEach((attachment) => {
    const fileExt = new RegExp(/\w{3,4}($|\?)/gmi)
    const type = fileExt.exec(attachment.url)
    formattedAttachments.push({
      id: attachment.id,
      name: attachment.description,
      path: getSecuredAssetUrl(attachment.url),
      type: type ? type[0] : 'URL'
    })
  })
  return formattedAttachments
}

export function removeElement (elementId: string) {
  const element = document.getElementById(elementId)
  if (element) element.remove()
}

export function removeDuplicate (list: Array<any>) {
  if (list.length === 1) return list
  const unique = []
  list.forEach(item => {
    if (!unique.some(key => JSON.stringify(key) === JSON.stringify(item))) unique.push(item)
  })
  return unique
}

export function extractObjectIds (item: Item) {
  const itemIds = _.compact(item.items.map(item => item.id))
  const groupIds = _.compact(item.itemGroups.map(item => item.group.id))
  const contentIds = _.compact(item.items.map(item => item.content.id))
  const assetIds = _.compact(_.flattenDepth(item.items.map(item => item.content.contentAssets.map(contentAsset => contentAsset.asset.id)), 2))

  const remainingParticipatingUserIds = _.compact(item.itemUsers.map(itemUser => itemUser.user.id))
  const remainingMaterialUserIds = _.compact(_.flattenDepth(item.items.map(item => item.itemUsers.map(itemUser => itemUser.user.id)), 2))

  return {
    itemIds,
    groupIds,
    contentIds,
    assetIds,
    remainingParticipatingUserIds,
    remainingMaterialUserIds
  }
}

export function extractAssetObjectIds (contentAssets: Array<ContentAsset>): Array<AssetId> {
  return _.compact(contentAssets.map(contentAsset => contentAsset.asset.id))
}

export async function getLatestClientVersion () {
  const result = await axiosHttp({
    method: 'get',
    url: '/api/v1/getClientVersion',
    headers: {
      'Cache-Control': 'no-store'
    }
  })

  if (result.status === 200) {
    return result.data.version
  } else {
    return result.data
  }
}

export function setProp (obj: object, props: Array<string>, value: any) {
  const prop = props.shift()
  if (!obj[prop]) {
    Vue.set(obj, prop, {})
  }
  if (!props.length) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Vue.set(obj, prop, { ...obj[prop], ...value })
    } else {
      Vue.set(obj, prop, value)
    }
    return
  }
  setProp(obj[prop], props, value)
}

export function deleteProp (obj: object, props: Array<string>) {
  const prop = props.shift()
  if (!obj[prop]) {
    return
  }
  if (!props.length) {
    Vue.delete(obj, prop)
    return
  }
  deleteProp(obj[prop], props)
}

export function omitProp (obj: any, prop: string, deep?: boolean): any {
  let newObj = {}
  // @ts-ignore
  newObj = _.omit(obj, prop)
  console.log('omitProp', prop, newObj)
  if (deep) {
    // @ts-ignore
    for (const newObjProp of _.keys(newObj)) {
      if (_.isObject(newObj[newObjProp])) newObj[newObjProp] = omitProp(newObj[newObjProp], prop, deep)
    }
  }
  return newObj
}

export function formatDate (strDate: string | number | Date, format?: string) {
  format = format || 'MMM DD, YYYY'
  if (strDate instanceof Number) {
    const dateTime = new Date(strDate)
    return moment(dateTime).format(format)
  } else {
    return moment(strDate).format(format)
  }
}

export function getServerUrl (): string {
  return process.env.DEV ? deployment.development.SERVER_URL : deployment[process.env.ENVIRONMENT].SERVER_URL
}

export function getFileUrl (filename: string): string {
  return `${getServerUrl()}/uploads/${filename}?token=${localStorage.getItem('token')}`
}

export function getFileName (file: BucketItem, path): string {
  if (file.name) {
    const displayName = file.name.replace(path, '')
    return displayName
  }
  if (file.prefix) return file.prefix.replace(this.currentPath, '').replace(/\//g, '')
  return ''
}

export function getTranslatedFileSize (filesize: number): number | string {
  if (!filesize) return 'Directory'
  return humanStorageSize(filesize)
}

export function getFileType (filename: string): string {
  return filename.split('.').pop().toLocaleLowerCase()
}

export function getFileAssetType (filename: string): string {
  let type
  switch (getFileType(filename)) {
    case 'doc':
    case 'docx':
    case 'pdf':
      type = AssetTypeEnum.PDF
      break
    case 'png':
    case 'jpg':
    case 'jpeg':
      type = AssetTypeEnum.IMAGE
      break
    case 'mp3':
      type = AssetTypeEnum.AUDIO
      break
    case 'mp4':
    case 'webm':
      type = AssetTypeEnum.VIDEO
      break
    case 'lnk':
      type = AssetTypeEnum.URL
      break
    default:
      type = 'ATTACHMENT'
  }
  return type
}

export function userRolesStringify (roles: Array<UserRole>): string {
  if (!roles || roles.length === 0) return ''
  const userRoles = []
  roles.forEach(role => {
    userRoles.push(role.role.name)
  })
  return userRoles.join(', ')
}

export async function isOnline () {
  if (!window.navigator.onLine) return false

  // avoid CORS errors with a request to your own origin
  const url = new URL(window.location.origin)

  const getRandomString = () => {
    return Math.random().toString(36).substring(2, 15)
  }
  // random value to prevent cached responses
  url.searchParams.set('rand', getRandomString())

  try {
    const response = await fetch(
      url.toString(),
      { method: 'HEAD' }
    )

    return response.ok
  } catch {
    return false
  }
}

export function networkHeartbeat () {
  return new Promise((resolve, reject) => {
    const pingUrl = process.env.DEV ? deployment.development.SERVER_URL : deployment[process.env.ENVIRONMENT].SERVER_URL
    fetch(`${pingUrl}/api/v1/check`)
      .then(response => resolve(response))
      .catch(error => {
        if (error.message.indexOf('Failed to fetch') !== -1) {
          EventBus.$emit('networkOffline')
          reject(error)
        }
      })
  })
}

export async function downloadFile (url: string): Promise<unknown> {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    try {
      const response = await fetch(url, { method: 'GET' })
      const contentLength = response.headers.get('content-length')
      const total = parseInt(contentLength, 10)
      let loaded = 0
      let disconnected = false
      const res = new Response(new ReadableStream({
        async start (controller) {
          const reader = response.body.getReader()
          const heartbeat = setInterval(() => {
            if (!window.navigator.onLine) {
              EventBus.$emit('networkOffline')
              disconnected = true
              clearInterval(heartbeat)
            }
          }, 5000)
          const progress = setInterval(() => {
            EventBus.$emit('downloadProgress', {
              url,
              progress: Math.round(loaded / total * 100)
            })
          }, 3000)
          for (;;) {
            const { done, value } = await reader.read()
            if (done || disconnected) {
              if (!disconnected) {
                EventBus.$emit('downloadProgress', {
                  url,
                  progress: 100
                })
              }
              break
            }
            loaded += value.byteLength
            controller.enqueue(value)
          }
          clearInterval(heartbeat)
          clearInterval(progress)
          controller.close()
        }
      }))
      const downloaded = await res.blob()
      if (downloaded && Math.round(loaded / total * 100) === 100) resolve(downloaded)
      else EventBus.$emit('networkOffline')
    } catch (error) {
      EventBus.$emit('networkOffline')
    }
  })
}

export function addToDownloadAssetList (source: Array<AssetUrl>, newUrl: Array<AssetUrl> | AssetUrl) {
  if (typeof newUrl === 'string') source.push(newUrl)
  else source = [...source, ...newUrl]
  return source
}

export function removeToDownloadAssetList (source: Array<AssetUrl>, newUrl: AssetUrl) {
  return source.filter(url => url !== newUrl)
}
